/**
 * Wage Report Builder — admin editor.
 * Grid is editable; sidebar runs live calculation and shows the result
 * card the client will see. Admin can pull from Xero/Prosaic, upload a
 * P&L, edit any cell, add notes, then Send Report to the client.
 */
import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import {
    Loader2, ArrowLeft, Save, Download, Mail, RefreshCw, Upload,
    Lock, FileEdit, ExternalLink, LayoutTemplate,
} from "lucide-react";
import { api, API_BASE, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

const CATEGORIES = [
    { key: "sales", label: "Sales", kind: "income" },
    { key: "other_income", label: "Other income", kind: "income" },
    { key: "subcontractors", label: "Subcontractors", kind: "expense" },
    { key: "staff_wages", label: "Staff wages", kind: "expense" },
    { key: "software", label: "Software", kind: "expense" },
    { key: "advertising", label: "Advertising", kind: "expense" },
    { key: "rent", label: "Rent", kind: "expense" },
    { key: "insurance", label: "Insurance", kind: "expense" },
    { key: "bank_fees", label: "Bank fees", kind: "expense" },
    { key: "phone_internet", label: "Phone & internet", kind: "expense" },
    { key: "travel", label: "Travel", kind: "expense" },
    { key: "other_expenses", label: "Other expenses", kind: "expense" },
];

const nz = (v, dp = 0) => v == null ? "—" : `$${Number(v).toLocaleString("en-NZ", { minimumFractionDigits: dp, maximumFractionDigits: dp })}`;

export default function WageReportBuilder() {
    const { id } = useParams();
    const nav = useNavigate();
    const fileRef = useRef(null);

    const [doc, setDoc] = useState(null);
    const [templates, setTemplates] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [pulling, setPulling] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [dragOver, setDragOver] = useState(false);
    const [sendOpen, setSendOpen] = useState(false);
    const [sending, setSending] = useState(false);
    const [detail, setDetail] = useState(false);
    const saveTimer = useRef(null);

    const load = useCallback(async () => {
        try {
            const { data } = await api.get(`/wage-reports/${id}`);
            setDoc(data);
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setLoading(false); }
    }, [id]);
    useEffect(() => { load(); }, [load]);

    useEffect(() => {
        api.get("/wage-report-templates").then(({ data }) => setTemplates(data || [])).catch(() => {});
    }, []);

    async function applyTemplate(tplId) {
        if (!tplId) return;
        try {
            const { data } = await api.post(`/wage-reports/${id}/apply-template`, null, { params: { template_id: tplId } });
            setDoc((prev) => ({ ...prev, ...data }));
            toast.success(`Applied template "${data.template_name}"`);
        } catch (e) { toast.error(formatApiError(e)); }
    }

    const locked = doc?.status === "sent";

    function patchLocal(update) {
        setDoc((d) => ({ ...d, ...update }));
    }

    async function pushSave(patch) {
        if (locked) return;
        setSaving(true);
        try {
            const { data } = await api.patch(`/wage-reports/${id}`, patch);
            // Keep client meta from the previously loaded doc.
            setDoc((prev) => ({ ...prev, ...data }));
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setSaving(false); }
    }

    function queueSave(patch) {
        patchLocal(patch);
        clearTimeout(saveTimer.current);
        saveTimer.current = setTimeout(() => pushSave(patch), 500);
    }

    function updateMonthCell(mIdx, key, value) {
        if (locked) return;
        const n = Math.max(0, Number(value.replace?.(/[^0-9.-]/g, "") ?? value) || 0);
        const months = (doc.months || []).map((m, i) => i === mIdx ? { ...m, [key]: n } : m);
        queueSave({ months });
    }

    function updateMonthLabel(mIdx, label) {
        if (locked) return;
        const months = (doc.months || []).map((m, i) => i === mIdx ? { ...m, label } : m);
        queueSave({ months });
    }

    function updateInput(key, value) {
        if (locked) return;
        const n = value === "" || value == null ? null : Number(value);
        queueSave({ inputs: { ...doc.inputs, [key]: n } });
    }

    function updateAdjustment(key, value) {
        if (locked) return;
        const n = value === "" || value == null ? null : (
            (key === "note" || key === "owner_pay_source" || key === "owners") ? value : Number(value)
        );
        queueSave({ adjustments: { ...(doc.adjustments || {}), [key]: n, auto_detected: key === "addback_owner_wages" ? false : doc.adjustments?.auto_detected } });
    }

    function updateOwners(owners, pay_source) {
        if (locked) return;
        const patch = { ...(doc.adjustments || {}), owners };
        if (pay_source !== undefined) patch.owner_pay_source = pay_source;
        queueSave({ adjustments: patch });
    }

    function updateCommentary(value) {
        if (locked) return;
        queueSave({ commentary: value });
    }

    async function pullSynced() {
        setPulling(true);
        try {
            const { data } = await api.post(`/wage-reports/${id}/pull`);
            setDoc((prev) => ({ ...prev, ...data }));
            toast.success(`Pulled ${data.source} P&L`);
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setPulling(false); }
    }

    async function uploadFile(file) {
        if (!file) return;
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const { data } = await api.post(`/wage-reports/${id}/upload`, fd);
            setDoc((prev) => ({ ...prev, ...data }));
            toast.success("P&L uploaded");
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setUploading(false); }
    }

    async function onUpload(e) {
        const file = e.target.files?.[0];
        e.target.value = "";
        await uploadFile(file);
    }

    function onDrop(e) {
        e.preventDefault();
        setDragOver(false);
        if (locked) return;
        const file = e.dataTransfer?.files?.[0];
        if (file) uploadFile(file);
    }

    async function revise() {
        try {
            const { data } = await api.post(`/wage-reports/${id}/revise`);
            setDoc((prev) => ({ ...prev, ...data }));
            toast.success(`Opened for editing · v${data.version}`);
        } catch (e) { toast.error(formatApiError(e)); }
    }

    function downloadPdf() {
        const token = window.localStorage.getItem("auth_token");
        const url = `${API_BASE}/wage-reports/${id}/pdf`;
        // Use fetch so Authorization header rides along, then blob + download.
        fetch(url, { headers: { Authorization: `Bearer ${token}` } })
            .then((r) => r.ok ? r.blob() : Promise.reject(new Error("Download failed")))
            .then((blob) => {
                const a = document.createElement("a");
                a.href = URL.createObjectURL(blob);
                a.download = `wage-report-${(doc?.client_name || "client").replace(/\W+/g, "-")}.pdf`;
                document.body.appendChild(a); a.click(); a.remove();
            })
            .catch(() => toast.error("PDF download failed"));
    }

    if (loading) return <div className="py-10 text-center text-[#5E636E]"><Loader2 className="inline h-4 w-4 animate-spin" /></div>;
    if (!doc) return <div className="py-10 text-[#5E636E]">Report not found.</div>;

    const result = doc.result || {};
    const months = doc.months || [];
    const inputs = doc.inputs || {};

    const hasPulled = ["prosaic", "xero"].includes(doc.source);

    return (
        <div className="space-y-5" data-testid="wage-report-builder">
            <header className="flex items-start justify-between gap-3 flex-wrap">
                <div className="flex items-start gap-3">
                    <Link to="/app/wage-reports" className="h-9 inline-flex items-center gap-1 rounded-md border border-[#E4E4E7] bg-white px-2 text-[12.5px] text-[#5E636E] hover:text-[#0F1115]" data-testid="back-to-list">
                        <ArrowLeft className="h-3.5 w-3.5" /> Back
                    </Link>
                    <div>
                        <div className="flex items-center gap-2">
                            <h1 className="text-xl font-semibold text-[#0F1115]">{doc.client_name || "Report"}</h1>
                            <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10.5px] font-semibold ${locked ? "bg-emerald-50 text-emerald-800 border-emerald-200" : "bg-amber-50 text-amber-800 border-amber-200"}`}>
                                {locked && <Lock className="h-2.5 w-2.5" />}{locked ? "Sent" : "Draft"}{doc.version > 1 ? ` · v${doc.version}` : ""}
                            </span>
                            {saving && <span className="text-[11px] text-[#5E636E]"><Loader2 className="inline h-3 w-3 animate-spin mr-1" />saving</span>}
                        </div>
                        <p className="text-[12.5px] text-[#5E636E] mt-0.5">
                            Source: {doc.source || "manual"}{doc.pulled_at ? ` · pulled ${new Date(doc.pulled_at).toLocaleString("en-NZ")}` : ""}{doc.sent_at ? ` · sent to ${doc.sent_to_email} on ${new Date(doc.sent_at).toLocaleString("en-NZ")}` : ""}
                        </p>
                    </div>
                </div>
                <div className="flex gap-2 flex-wrap justify-end">
                    {!locked && templates.length > 0 && (
                        <select onChange={(e) => applyTemplate(e.target.value)} value=""
                            className="h-9 rounded-md border border-[#E4E4E7] bg-white px-2 text-[12.5px] text-[#0F1115]"
                            data-testid="wage-report-apply-template">
                            <option value="">Apply template…</option>
                            {templates.map((t) => (
                                <option key={t.id} value={t.id}>{t.name}{t.is_default ? " · default" : ""}</option>
                            ))}
                        </select>
                    )}
                    {!locked && (
                        <>
                            <Button variant="outline" size="sm" onClick={pullSynced} disabled={pulling} className="h-9 text-[12.5px]" data-testid="wage-report-pull">
                                {pulling ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <RefreshCw className="h-3.5 w-3.5 mr-1" />}
                                Pull from Xero/Prosaic
                            </Button>
                            <Button variant="outline" size="sm" onClick={() => fileRef.current?.click()} disabled={uploading} className="h-9 text-[12.5px]" data-testid="wage-report-upload">
                                {uploading ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Upload className="h-3.5 w-3.5 mr-1" />}
                                Upload P&L
                            </Button>
                            <input ref={fileRef} type="file" accept=".xlsx,.xls,.csv,.pdf,.png,.jpg,.jpeg,.webp" className="hidden" onChange={onUpload} data-testid="wage-report-file-input" />
                        </>
                    )}
                    <Button variant="outline" size="sm" onClick={downloadPdf} className="h-9 text-[12.5px]" data-testid="wage-report-pdf">
                        <Download className="h-3.5 w-3.5 mr-1" /> Preview PDF
                    </Button>
                    {locked ? (
                        <Button size="sm" onClick={revise} className="h-9 text-[12.5px] bg-[#0F1115] hover:bg-[#2E2E2E] text-white" data-testid="wage-report-revise">
                            <FileEdit className="h-3.5 w-3.5 mr-1" /> Revise
                        </Button>
                    ) : (
                        <Button size="sm" onClick={() => setSendOpen(true)} className="h-9 text-[12.5px] bg-[#0F1115] hover:bg-[#2E2E2E] text-white" data-testid="wage-report-send">
                            <Mail className="h-3.5 w-3.5 mr-1" /> Send to client
                        </Button>
                    )}
                </div>
            </header>

            {doc.sent_at && locked && (
                <div className="rounded-md border border-emerald-200 bg-emerald-50 p-3 text-[12.5px] text-emerald-900 flex items-center justify-between gap-3 flex-wrap" data-testid="wage-report-sent-banner">
                    <div>
                        <b>Sent to {doc.sent_to_email}</b> on {new Date(doc.sent_at).toLocaleString("en-NZ")}. The client can view it at
                        <code className="ml-1 text-[11px] bg-white border border-emerald-200 rounded px-1 py-0.5">/portal/wage-report/{doc.portal_token?.slice(0, 10)}…</code>
                    </div>
                    <a href={`/portal/wage-report/${doc.portal_token}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 underline hover:no-underline">
                        <ExternalLink className="h-3 w-3" /> Open link
                    </a>
                </div>
            )}

            <div
                className="grid lg:grid-cols-[1fr_360px] gap-5 relative"
                onDragOver={(e) => { if (!locked) { e.preventDefault(); setDragOver(true); } }}
                onDragLeave={() => setDragOver(false)}
                onDrop={onDrop}
                data-testid="wage-report-dropzone"
            >
                {dragOver && !locked && (
                    <div className="absolute inset-0 z-20 rounded-lg border-2 border-dashed border-[#0F1115] bg-[#F7F6F2]/90 flex items-center justify-center pointer-events-none">
                        <div className="text-center">
                            <Upload className="h-8 w-8 mx-auto text-[#0F1115]" />
                            <div className="mt-2 font-medium text-[#0F1115]">Drop file to upload</div>
                            <div className="text-[12.5px] text-[#5E636E]">Excel · CSV · PDF · screenshot (PNG / JPG / WebP)</div>
                        </div>
                    </div>
                )}
                {/* Left: editable grid + inputs */}
                <div className="space-y-5">
                    <section className="rounded-md border border-[#E4E4E7] bg-white">
                        <header className="px-4 py-3 border-b border-[#EEEDE7] flex items-center justify-between gap-2">
                            <div>
                                <div className="font-medium text-[13px] text-[#0F1115]">P&L — editable grid</div>
                                <div className="text-[11.5px] text-[#5E636E]">All amounts GST-exclusive. Owner's drawings / shareholder salary are stripped out automatically. Drag &amp; drop an Excel, CSV, PDF, or screenshot anywhere on this page to auto-fill.</div>
                            </div>
                            <div className="flex items-center gap-3">
                                <button type="button" onClick={() => setDetail((d) => !d)}
                                    className="text-[11.5px] text-[#5E636E] hover:text-[#0F1115] underline underline-offset-2"
                                    data-testid="toggle-pl-detail">
                                    {detail ? "Show simple view" : "Show account detail"}
                                </button>
                                <div className="text-[11px] text-[#5E636E]">{months.length} months</div>
                            </div>
                        </header>
                        <div className="overflow-x-auto">
                            <table className="w-full text-[12.5px]">
                                <thead>
                                    <tr className="text-[10.5px] uppercase tracking-wider text-[#5E636E]">
                                        <th className="text-left px-3 py-2 sticky left-0 bg-white z-10 border-r border-[#EEEDE7]">Category</th>
                                        {months.map((m, i) => (
                                            <th key={i} className="text-right px-2 py-2 min-w-[110px]">
                                                <Input value={m.label || ""} onChange={(e) => updateMonthLabel(i, e.target.value)} readOnly={locked}
                                                    className="h-7 text-[11px] text-right bg-white" data-testid={`month-label-${i}`} />
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {detail ? CATEGORIES.map((cat) => (
                                        <tr key={cat.key} className={`border-t border-[#EEEDE7] ${cat.kind === "income" ? "bg-emerald-50/40" : ""}`}>
                                            <td className="px-3 py-1.5 sticky left-0 bg-inherit z-10 border-r border-[#EEEDE7] font-medium text-[#0F1115]">
                                                {cat.label}
                                                <span className="ml-1 text-[10px] uppercase text-[#5E636E] font-normal">{cat.kind}</span>
                                            </td>
                                            {months.map((m, i) => (
                                                <td key={i} className="px-1 py-1">
                                                    <Input value={m[cat.key] ?? 0} onChange={(e) => updateMonthCell(i, cat.key, e.target.value)} readOnly={locked}
                                                        className="h-7 text-right text-[12px] tabular-nums bg-transparent border-transparent focus:border-[#D4D4D8] focus:bg-white" data-testid={`cell-${cat.key}-${i}`} />
                                                </td>
                                            ))}
                                        </tr>
                                    )) : (
                                        <SimpleGridRows months={months} result={result} locked={locked} updateMonthCell={updateMonthCell} />
                                    )}
                                    <tr className="border-t-2 border-[#D4D4D8] bg-zinc-50">
                                        <td className="px-3 py-2 font-semibold text-[#0F1115]">Net profit</td>
                                        {(result.monthly_net_profit || []).map((v, i) => (
                                            <td key={i} className="px-2 py-2 text-right font-semibold tabular-nums">{nz(v, 0)}</td>
                                        ))}
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                        {!!(doc.owner_pay_lines?.length) && (
                            <div className="px-4 py-3 border-t border-[#EEEDE7] bg-amber-50/50 text-[12px] text-amber-900">
                                <b>{doc.owner_pay_lines.length}</b> owner-pay line{doc.owner_pay_lines.length > 1 ? "s" : ""} excluded: {doc.owner_pay_lines.map(l => l.label).join(" · ")}
                            </div>
                        )}
                    </section>

                    <OwnersSection
                        locked={locked}
                        adjustments={doc.adjustments || {}}
                        onOwnersChange={updateOwners}
                        onChange={updateAdjustment}
                    />

                    <AdjustmentsSection
                        locked={locked}
                        adjustments={doc.adjustments || {}}
                        result={result}
                        onChange={updateAdjustment}
                    />

                    <section className="rounded-md border border-[#E4E4E7] bg-white">
                        <header className="px-4 py-3 border-b border-[#EEEDE7]">
                            <div className="font-medium text-[13px] text-[#0F1115]">Inputs</div>
                            <div className="text-[11.5px] text-[#5E636E]">Shape the fair wage benchmark and retention policy.</div>
                        </header>
                        <div className="p-4 grid grid-cols-2 md:grid-cols-3 gap-3 text-[12.5px]">
                            <InputRow label="Hours per week" value={inputs.hours_per_week} onChange={(v) => updateInput("hours_per_week", v)} locked={locked} testid="in-hours" />
                            <InputRow label="Market $/hour" value={inputs.market_hourly_rate} onChange={(v) => updateInput("market_hourly_rate", v)} locked={locked} testid="in-rate" />
                            <InputRow label="Retained profit %" value={inputs.retained_profit_pct != null ? Number(inputs.retained_profit_pct) * 100 : null}
                                onChange={(v) => updateInput("retained_profit_pct", v === null ? null : Number(v) / 100)} locked={locked} suffix="%" testid="in-retained" />
                            <InputRow label="Avg monthly fee / client" value={inputs.avg_monthly_fee_per_client} onChange={(v) => updateInput("avg_monthly_fee_per_client", v)} locked={locked} testid="in-fee" />
                            <InputRow label="Cash at bank" value={inputs.cash_at_bank} onChange={(v) => updateInput("cash_at_bank", v)} locked={locked} testid="in-cash" />
                            <InputRow label="Min cash buffer" value={inputs.min_cash_buffer} onChange={(v) => updateInput("min_cash_buffer", v)} locked={locked} testid="in-buffer" />
                        </div>
                    </section>

                    <section className="rounded-md border border-[#E4E4E7] bg-white">
                        <header className="px-4 py-3 border-b border-[#EEEDE7]">
                            <div className="font-medium text-[13px] text-[#0F1115]">Your notes to the client</div>
                            <div className="text-[11.5px] text-[#5E636E]">Shown inline in the PDF and the portal view.</div>
                        </header>
                        <div className="p-4">
                            <Textarea rows={4} value={doc.commentary || ""} onChange={(e) => updateCommentary(e.target.value)} readOnly={locked}
                                placeholder="e.g. Based on the last 4 months we recommend you lift to $1,250/wk from April. We'll revisit at your mid-year check-in." data-testid="wage-report-notes" />
                        </div>
                    </section>
                </div>

                {/* Right: result card (what the client will see) */}
                <aside className="space-y-4">
                    <div className="sticky top-4 space-y-4">
                        <OwnerPayPositionCard result={result} />

                        {(result.extra_revenue_monthly > 0 || result.extra_clients) && (
                            <div className="rounded-md border border-[#E4E4E7] bg-white p-4 text-[12.5px]">
                                <div className="font-medium text-[#0F1115] mb-1">To reach fair market pay</div>
                                <div className="text-[#5E636E]">Current affordable: <b className="text-[#0F1115]">{nz(result.total_affordable_weekly || result.affordable_weekly)}</b>/wk</div>
                                <div className="text-[#5E636E]">Fair market target: <b className="text-[#0F1115]">{nz(result.fair_weekly)}</b>/wk</div>
                                {result.extra_revenue_monthly > 0 && <div className="text-[#5E636E] mt-1">Extra monthly revenue required: <b className="text-[#0F1115]">{nz(result.extra_revenue_monthly)}</b></div>}
                                {result.extra_clients != null && <div className="text-[#5E636E]">≈ {result.extra_clients} more clients at your typical fee</div>}
                            </div>
                        )}

                        <div className="rounded-md border border-[#E4E4E7] bg-[#F7F6F2] p-3 text-[11px] text-[#5E636E] leading-relaxed">
                            This report is a management estimate based on the financial information provided. Tax, GST, PAYE, debt repayments, capital expenditure and seasonal changes may affect the amount actually available to withdraw.
                        </div>
                    </div>
                </aside>
            </div>

            <SendDialog
                open={sendOpen}
                onClose={() => setSendOpen(false)}
                defaultEmail={doc.client_email}
                defaultName={doc.client_name}
                templates={templates}
                sending={sending}
                onSend={async ({ to_email, to_name, message }) => {
                    setSending(true);
                    try {
                        const { data } = await api.post(`/wage-reports/${id}/send`, { to_email, to_name, message });
                        setDoc((prev) => ({ ...prev, ...data }));
                        toast.success(`Report emailed to ${to_email}`);
                        setSendOpen(false);
                    } catch (err) { toast.error(formatApiError(err)); }
                    finally { setSending(false); }
                }}
            />
        </div>
    );
}

function SimpleGridRows({ months, result, locked, updateMonthCell }) {
    // Flatten the 12 categories into three rows the owner can read:
    //   Income        = sales + other_income (edits write to `sales`)
    //   Staff wages   = staff_wages (kept visible — it feeds the owner-pay add-back)
    //   Expenses      = everything else combined (edits write to `other_expenses`)
    // Flip into "Show account detail" from the header to see / edit the raw buckets.
    const expenseKeys = CATEGORIES.filter((c) => c.kind === "expense" && c.key !== "staff_wages").map((c) => c.key);
    const incomeTotal = (m) => CATEGORIES.filter((c) => c.kind === "income").reduce((s, c) => s + Number(m[c.key] || 0), 0);
    const expenseTotal = (m) => expenseKeys.reduce((s, k) => s + Number(m[k] || 0), 0);

    const simpleRows = [
        { key: "income", label: "Income", editKey: "sales", kind: "income", total: incomeTotal, hint: "All sales + other income" },
        { key: "staff_wages", label: "Staff wages", editKey: "staff_wages", kind: "expense", total: (m) => Number(m.staff_wages || 0), hint: "Keep separate — used to identify owner pay" },
        { key: "expenses", label: "All other expenses", editKey: "other_expenses", kind: "expense", total: expenseTotal, hint: "Everything except staff wages" },
    ];

    return (
        <>
            {simpleRows.map((row) => (
                <tr key={row.key} className={`border-t border-[#EEEDE7] ${row.kind === "income" ? "bg-emerald-50/40" : ""}`}>
                    <td className="px-3 py-1.5 sticky left-0 bg-inherit z-10 border-r border-[#EEEDE7] font-medium text-[#0F1115]">
                        {row.label}
                        <div className="text-[10px] uppercase text-[#5E636E] font-normal">{row.hint}</div>
                    </td>
                    {months.map((m, i) => {
                        const total = row.total(m);
                        const direct = Number(m[row.editKey] || 0);
                        // If the row aggregates OTHER keys (income or expenses mix),
                        // show the summed read-only total but let the admin edit the
                        // single bucket this row writes to.
                        const hasExtraBuckets = total !== direct;
                        return (
                            <td key={i} className="px-1 py-1">
                                {hasExtraBuckets ? (
                                    <div className="h-7 flex items-center justify-end px-2 text-[12px] tabular-nums text-[#0F1115]"
                                        title={`${nz(direct)} typed + ${nz(total - direct)} from account detail`}>
                                        {nz(total)}
                                    </div>
                                ) : (
                                    <Input value={m[row.editKey] ?? 0}
                                        onChange={(e) => updateMonthCell(i, row.editKey, e.target.value)}
                                        readOnly={locked}
                                        className="h-7 text-right text-[12px] tabular-nums bg-transparent border-transparent focus:border-[#D4D4D8] focus:bg-white"
                                        data-testid={`cell-simple-${row.key}-${i}`} />
                                )}
                            </td>
                        );
                    })}
                </tr>
            ))}
        </>
    );
}

function OwnersSection({ locked, adjustments, onOwnersChange, onChange }) {
    const owners = Array.isArray(adjustments.owners) ? adjustments.owners : [];
    const source = adjustments.owner_pay_source || "not_included";

    function setCount(n) {
        if (locked) return;
        const next = [...owners];
        while (next.length < n) next.push({ name: "", weekly_pay: 0, monthly_pay: null, allocation_pct: null });
        while (next.length > n) next.pop();
        onOwnersChange(next);
    }
    function patchOwner(idx, patch) {
        if (locked) return;
        const next = owners.map((o, i) => i === idx ? { ...o, ...patch } : o);
        onOwnersChange(next);
    }

    const count = owners.length || 0;

    return (
        <section className="rounded-md border border-[#E4E4E7] bg-white" data-testid="owners-section">
            <header className="px-4 py-3 border-b border-[#EEEDE7]">
                <div className="font-medium text-[13px] text-[#0F1115]">Owner pay already included in P&amp;L</div>
                <div className="text-[11.5px] text-[#5E636E]">Identify how much of the P&amp;L expense belongs to the working owners. We use this to measure profit <i>before</i> owner pay and to compare total affordable pay with what the owners are currently receiving.</div>
            </header>
            <div className="p-4 space-y-4 text-[12.5px]">
                <div>
                    <div className="text-[10.5px] uppercase tracking-wider text-[#5E636E] mb-1">Where is owner pay shown on this P&amp;L?</div>
                    <div className="flex flex-wrap gap-3">
                        {[
                            { k: "in_staff_wages", label: "Mixed into Staff wages" },
                            { k: "elsewhere", label: "On another P&L line" },
                            { k: "not_included", label: "NOT included in this P&L" },
                        ].map((opt) => (
                            <label key={opt.k} className="inline-flex items-center gap-2 cursor-pointer">
                                <input type="radio" name="owner_pay_source" disabled={locked}
                                    checked={source === opt.k}
                                    onChange={() => onChange("owner_pay_source", opt.k)}
                                    data-testid={`owner-pay-source-${opt.k}`} />
                                <span className="text-[12.5px] text-[#0F1115]">{opt.label}</span>
                            </label>
                        ))}
                    </div>
                </div>

                <div className="flex items-center gap-2">
                    <div className="text-[10.5px] uppercase tracking-wider text-[#5E636E]">Number of working owners</div>
                    <div className="flex gap-1">
                        {[1, 2, 3].map((n) => (
                            <button key={n} type="button" disabled={locked}
                                onClick={() => setCount(n)}
                                className={`h-7 min-w-[32px] rounded border px-2 text-[12px] ${count === n ? "bg-[#0F1115] text-white border-[#0F1115]" : "bg-white text-[#0F1115] border-[#E4E4E7] hover:bg-zinc-50"}`}
                                data-testid={`owner-count-${n}`}>
                                {n}
                            </button>
                        ))}
                        <Input type="number" min="0" max="10" value={count > 3 ? count : ""} placeholder="Custom"
                            onChange={(e) => setCount(Math.max(0, Math.min(10, Number(e.target.value) || 0)))}
                            readOnly={locked} className="h-7 w-20 text-[12px]" data-testid="owner-count-custom" />
                    </div>
                </div>

                {count > 0 && (
                    <div className="rounded-md border border-[#EEEDE7] overflow-hidden">
                        <table className="w-full text-[12.5px]">
                            <thead>
                                <tr className="bg-[#F7F6F2] text-[10.5px] uppercase tracking-wider text-[#5E636E]">
                                    <th className="text-left px-3 py-2">#</th>
                                    <th className="text-left px-3 py-2">Name (optional)</th>
                                    <th className="text-right px-3 py-2">Weekly pay $</th>
                                    <th className="text-right px-3 py-2">Monthly pay $</th>
                                    <th className="text-right px-3 py-2">Allocation %</th>
                                </tr>
                            </thead>
                            <tbody>
                                {owners.map((o, i) => (
                                    <tr key={i} className="border-t border-[#EEEDE7]">
                                        <td className="px-3 py-1.5 text-[#5E636E]">Owner {i + 1}</td>
                                        <td className="px-3 py-1.5">
                                            <Input value={o.name || ""} onChange={(e) => patchOwner(i, { name: e.target.value })}
                                                readOnly={locked} className="h-7 text-[12px]" data-testid={`owner-${i}-name`} />
                                        </td>
                                        <td className="px-3 py-1.5">
                                            <Input value={o.weekly_pay ?? ""} onChange={(e) => patchOwner(i, { weekly_pay: e.target.value === "" ? null : Number(e.target.value), monthly_pay: null })}
                                                readOnly={locked || source === "not_included"} className="h-7 text-[12px] text-right tabular-nums" data-testid={`owner-${i}-weekly`} />
                                        </td>
                                        <td className="px-3 py-1.5">
                                            <Input value={o.monthly_pay ?? ""} onChange={(e) => patchOwner(i, { monthly_pay: e.target.value === "" ? null : Number(e.target.value), weekly_pay: e.target.value === "" ? null : Number(e.target.value) * 12 / 52 })}
                                                readOnly={locked || source === "not_included"} className="h-7 text-[12px] text-right tabular-nums" data-testid={`owner-${i}-monthly`} placeholder="(or enter weekly)" />
                                        </td>
                                        <td className="px-3 py-1.5">
                                            <Input value={o.allocation_pct ?? ""} onChange={(e) => patchOwner(i, { allocation_pct: e.target.value === "" ? null : Number(e.target.value) })}
                                                readOnly={locked} className="h-7 text-[12px] text-right tabular-nums" placeholder="Equal split" data-testid={`owner-${i}-alloc`} />
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
                {source === "not_included" && count > 0 && (
                    <div className="rounded-md border border-sky-300 bg-sky-50 p-2 text-[12px] text-sky-900">
                        Owner pay is NOT in this P&amp;L — current weekly pay is forced to $0 and no add-back is applied. The affordable number below is what the business could sustainably pay the owner(s).
                    </div>
                )}
                {(source === "in_staff_wages" || source === "elsewhere") && count > 0 && (
                    <div className="rounded-md border border-amber-300 bg-amber-50 p-2 text-[12px] text-amber-900">
                        We'll add back {count} × weekly pay × 52 as the Owner wages add-back below. Override that number manually if some owner pay belongs to shareholder salary accrued but unpaid.
                    </div>
                )}
            </div>
        </section>
    );
}

function OwnerPayPositionCard({ result }) {
    const r = result || {};
    const status = r.adjustment_status || "unknown";
    const adj = Number(r.recommended_adjustment_weekly || 0);
    const palette = status === "below_affordable_level"
        ? { border: "border-emerald-500", chip: "bg-emerald-50 text-emerald-900 border-emerald-200", num: "text-emerald-700", label: "ROOM TO INCREASE OWNER PAY" }
        : status === "above_affordable_level"
            ? { border: "border-red-500", chip: "bg-red-50 text-red-900 border-red-200", num: "text-red-700", label: "OWNER PAY ABOVE CURRENT AFFORDABLE LEVEL" }
            : status === "within"
                ? { border: "border-sky-500", chip: "bg-sky-50 text-sky-900 border-sky-200", num: "text-sky-700", label: "WITHIN AFFORDABLE RANGE" }
                : { border: "border-[#0F1115]", chip: "bg-zinc-50 text-[#0F1115] border-[#E4E4E7]", num: "text-[#0F1115]", label: "ENTER OWNER PAY TO COMPARE" };
    const sign = adj > 0 ? "+" : adj < 0 ? "−" : "";
    const breakdown = Array.isArray(r.owner_breakdown) ? r.owner_breakdown : [];
    return (
        <div className={`rounded-md border-2 ${palette.border} bg-white p-4`} data-testid="owner-pay-position">
            <div className="text-[10.5px] uppercase tracking-wider text-[#5E636E]">Owner pay position</div>

            <div className="mt-2 grid grid-cols-2 gap-3 text-[12px]">
                <div>
                    <div className="text-[10px] uppercase tracking-wider text-[#5E636E]">Affordable</div>
                    <div className="text-lg font-bold text-[#0F1115] tabular-nums" data-testid="position-affordable">
                        {nz(r.total_affordable_weekly || r.affordable_weekly)}/wk
                    </div>
                </div>
                <div>
                    <div className="text-[10px] uppercase tracking-wider text-[#5E636E]">Currently paid</div>
                    <div className="text-lg font-bold text-[#0F1115] tabular-nums" data-testid="position-current">
                        {nz(r.total_current_owner_pay_weekly || 0)}/wk
                    </div>
                </div>
            </div>

            <div className="mt-3 pt-3 border-t border-[#EEEDE7]">
                <div className="text-[10.5px] uppercase tracking-wider text-[#5E636E]">Recommended adjustment</div>
                <div className={`mt-1 text-3xl font-bold tabular-nums ${palette.num}`} data-testid="position-adjustment">
                    {sign}{nz(Math.abs(adj))}/wk
                </div>
                <div className={`inline-flex mt-1 rounded-full border px-2 py-0.5 text-[10.5px] font-semibold ${palette.chip}`}>
                    {palette.label}
                </div>
            </div>

            {r.owner_count > 1 && breakdown.length > 1 && (
                <div className="mt-3 pt-3 border-t border-[#EEEDE7] text-[11.5px]">
                    <div className="text-[10.5px] uppercase tracking-wider text-[#5E636E] mb-1">Per owner (combined for {r.owner_count})</div>
                    <ul className="space-y-1">
                        {breakdown.map((o, i) => (
                            <li key={i} className="flex justify-between text-[#0F1115] tabular-nums">
                                <span className="text-[#5E636E]">{o.name || `Owner ${i + 1}`} · {o.allocation_pct}%</span>
                                <span><b>{nz(o.affordable_weekly)}</b>/wk</span>
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            <div className="mt-3 pt-3 border-t border-[#EEEDE7] text-[11.5px] text-[#5E636E] space-y-1">
                <div>Fair market (benchmark): <b className="text-[#0F1115]">{nz(r.fair_weekly)}</b>/wk</div>
                <div>Gap to market: <b className="text-[#0F1115]">{nz(Math.abs(r.gap_to_fair_weekly || 0))}</b>/wk {Number(r.gap_to_fair_weekly || 0) < 0 ? "short" : "ahead"}</div>
                <div>Confidence: <b className="text-[#0F1115] capitalize">{r.confidence || "moderate"}</b>
                    {(r.confidence_reasons && r.confidence_reasons.length > 0) && (
                        <span className="text-[#5E636E]"> — {r.confidence_reasons.join("; ")}</span>
                    )}
                </div>
            </div>
        </div>
    );
}

function InputRow({ label, value, onChange, locked, suffix, testid }) {
    return (
        <label className="block">
            <div className="text-[10.5px] uppercase tracking-wider text-[#5E636E]">{label}</div>
            <div className="relative mt-1">
                <Input value={value ?? ""} onChange={(e) => onChange(e.target.value === "" ? null : e.target.value)} readOnly={locked}
                    className="h-8 text-[12.5px] text-right tabular-nums pr-6" data-testid={testid} />
                {suffix && <span className="absolute right-2 top-1.5 text-[11px] text-[#5E636E]">{suffix}</span>}
            </div>
        </label>
    );
}

function AdjustmentsSection({ locked, adjustments, result, onChange }) {
    const warnOwnerPay = result.owner_pay_detected || result.net_loss_detected;
    const warnOverExpenses = result.addbacks_exceed_expenses;
    const nzFmt = (v, dp = 2) => v == null ? "—" : `$${Number(v).toLocaleString("en-NZ", { minimumFractionDigits: dp, maximumFractionDigits: dp })}`;
    return (
        <section className="rounded-md border border-[#E4E4E7] bg-white" data-testid="adjustments-section">
            <header className="px-4 py-3 border-b border-[#EEEDE7]">
                <div className="font-medium text-[13px] text-[#0F1115]">Adjustments — profit before owner pay</div>
                <div className="text-[11.5px] text-[#5E636E]">Add back anything that distorts "real" profit. The Owner wages add-back is auto-filled from the owners you listed above (edit to override). Drawings are NOT a P&amp;L expense — don't add them here.</div>
            </header>

            {warnOwnerPay && (
                <div className="mx-4 mt-3 rounded-md border border-amber-300 bg-amber-50 p-2 text-[12px] text-amber-900">
                    <b>Heads up:</b> {result.net_loss_detected ? "the P&L shows a net loss. " : ""}If your P&L already includes your own wages, add them back below so we measure profit <i>before</i> owner pay.
                </div>
            )}
            {adjustments.auto_detected && (adjustments.addback_owner_wages || 0) > 0 && (
                <div className="mx-4 mt-3 rounded-md border border-sky-300 bg-sky-50 p-2 text-[12px] text-sky-900">
                    <b>Auto-detected</b>: we pre-filled Owner wages add-back from the owner-pay lines in the file. Please confirm the number is right.
                </div>
            )}
            {warnOverExpenses && (
                <div className="mx-4 mt-3 rounded-md border border-amber-300 bg-amber-50 p-2 text-[12px] text-amber-900">
                    <b>Check:</b> your add-backs ({nzFmt(result.total_addbacks)}) exceed total operating expenses ({nzFmt(result.total_expenses)}). Review before sending.
                </div>
            )}

            <div className="p-4 grid grid-cols-2 md:grid-cols-3 gap-3 text-[12.5px]">
                <InputRow label="Owner wages / director fees (auto)" value={adjustments.addback_owner_wages}
                    onChange={(v) => onChange("addback_owner_wages", v)} locked={locked} testid="adj-owner-wages" />
                <InputRow label="Employer on-cost on owner wages" value={adjustments.addback_owner_oncosts}
                    onChange={(v) => onChange("addback_owner_oncosts", v)} locked={locked} testid="adj-owner-oncosts" />
                <InputRow label="Private expenses" value={adjustments.addback_private_expenses}
                    onChange={(v) => onChange("addback_private_expenses", v)} locked={locked} testid="adj-private" />
                <InputRow label="One-off expenses to add back" value={adjustments.addback_one_off_expenses}
                    onChange={(v) => onChange("addback_one_off_expenses", v)} locked={locked} testid="adj-oneoff-exp" />
                <InputRow label="One-off income to remove" value={adjustments.remove_one_off_income}
                    onChange={(v) => onChange("remove_one_off_income", v)} locked={locked} testid="adj-oneoff-inc" />
            </div>

            <div className="px-4 pb-4">
                <div className="text-[10.5px] uppercase tracking-wider text-[#5E636E]">Reason for adjustments <span className="text-red-600">* required for staff</span></div>
                <Textarea rows={2} value={adjustments.note || ""} onChange={(e) => onChange("note", e.target.value)} readOnly={locked}
                    placeholder="e.g. Owner wages & KiwiSaver addback; one-off asset sale removed."
                    data-testid="adj-note" className="mt-1" />
            </div>

            {/* Reconciliation panel */}
            <div className="mx-4 mb-4 rounded-md border border-[#E4E4E7] bg-[#F7F6F2] p-3 text-[12px]">
                <div className="text-[10.5px] uppercase tracking-wider text-[#5E636E] mb-2">Reconciliation — profit before owner pay</div>
                <div className="space-y-1 tabular-nums">
                    <div className="flex justify-between"><span>Net profit per P&amp;L</span><b className={result.net_profit_per_pl < 0 ? "text-red-700" : "text-[#0F1115]"}>{nzFmt(result.net_profit_per_pl)}</b></div>
                    <div className="flex justify-between text-[#5E636E]"><span>+ Owner wages / director fees</span><span>{nzFmt(adjustments.addback_owner_wages || 0)}</span></div>
                    <div className="flex justify-between text-[#5E636E]"><span>+ Owner on-costs</span><span>{nzFmt(adjustments.addback_owner_oncosts || 0)}</span></div>
                    <div className="flex justify-between text-[#5E636E]"><span>+ Private expenses</span><span>{nzFmt(adjustments.addback_private_expenses || 0)}</span></div>
                    <div className="flex justify-between text-[#5E636E]"><span>+ One-off expenses</span><span>{nzFmt(adjustments.addback_one_off_expenses || 0)}</span></div>
                    <div className="flex justify-between text-[#5E636E]"><span>− One-off income</span><span>{nzFmt(adjustments.remove_one_off_income || 0)}</span></div>
                    <div className="flex justify-between pt-2 mt-1 border-t border-[#E4E4E7] font-semibold text-[#0F1115]">
                        <span>Profit before owner pay (used in formulas)</span>
                        <span data-testid="recon-adjusted">{nzFmt(result.adjusted_net_profit)}</span>
                    </div>
                </div>
            </div>

            {Array.isArray(adjustments.auto_detected) ? null : null}
        </section>
    );
}

function SendDialog({ open, onClose, defaultEmail, defaultName, templates = [], sending, onSend }) {
    const [to, setTo] = useState("");
    const [name, setName] = useState("");
    const [msg, setMsg] = useState("");
    const [tplId, setTplId] = useState("");
    useEffect(() => {
        if (open) {
            setTo(defaultEmail || "");
            setName(defaultName || "");
            // Pre-fill the message from the default template if there is one.
            const def = (templates || []).find((t) => t.is_default && t.email_message);
            setMsg(def?.email_message || "");
            setTplId(def?.id || "");
        }
    }, [open, defaultEmail, defaultName, templates]);
    function pickTemplate(id) {
        setTplId(id);
        if (!id) return;
        const t = templates.find((x) => x.id === id);
        if (t?.email_message != null) setMsg(t.email_message || "");
    }
    return (
        <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
            <DialogContent className="max-w-lg" data-testid="send-dialog">
                <DialogHeader>
                    <DialogTitle>Send Wage Report to client</DialogTitle>
                </DialogHeader>
                <div className="space-y-3">
                    <div>
                        <Label>Email</Label>
                        <Input value={to} onChange={(e) => setTo(e.target.value)} type="email" data-testid="send-email" />
                    </div>
                    <div>
                        <Label>Recipient name</Label>
                        <Input value={name} onChange={(e) => setName(e.target.value)} data-testid="send-name" />
                    </div>
                    {templates.length > 0 && (
                        <div>
                            <Label>Use a saved message template</Label>
                            <select value={tplId} onChange={(e) => pickTemplate(e.target.value)}
                                className="mt-1 w-full h-9 rounded-md border border-[#E4E4E7] bg-white px-3 text-[13px]"
                                data-testid="send-template">
                                <option value="">— None —</option>
                                {templates.filter((t) => t.email_message).map((t) => (
                                    <option key={t.id} value={t.id}>{t.name}{t.is_default ? " · default" : ""}</option>
                                ))}
                            </select>
                        </div>
                    )}
                    <div>
                        <Label>Optional note (shown in the email)</Label>
                        <Textarea rows={5} value={msg} onChange={(e) => setMsg(e.target.value)} placeholder="e.g. Here's the wage breakdown we discussed on Tuesday. Any questions, just reply."
                            data-testid="send-message" />
                    </div>
                    <div className="text-[11.5px] text-[#5E636E] rounded-md border border-[#E4E4E7] bg-zinc-50 p-2">
                        The email will include a secure portal link + the PDF attached. The report will lock to <b>Sent</b> after delivery.
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={onClose}>Cancel</Button>
                    <Button className="bg-[#0F1115] hover:bg-[#2E2E2E] text-white" disabled={sending || !to} onClick={() => onSend({ to_email: to, to_name: name, message: msg })} data-testid="send-confirm">
                        {sending ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Mail className="h-3.5 w-3.5 mr-1" />}
                        Send now
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
